import api from '../utils/axios';
import type { QueueState } from '../types/queue';

/**
 * Queue routes are per-agent: web build mode queues under the dev-agent prefix,
 * low-code under its own. Every call takes the base explicitly so one client
 * can never read one product's queue and write to the other's.
 */
export const DEV_QUEUE_BASE = (projectId: string) =>
  `/dev-agent/projects/${projectId}/queue`;

export const TAP_QUEUE_BASE = (projectId: string) =>
  `/projects/${projectId}/tap-agent/build/queue`;

export type QueueBase = (projectId: string) => string;

/**
 * Fetch the current queue state for a project.
 * Returns jobs in statuses: CURRENT, QUEUED, PAUSED, FAILED, STOPPED.
 */
export async function getQueueState(
  projectId: string,
  base: QueueBase = DEV_QUEUE_BASE
): Promise<QueueState> {
  const response = await api.get(base(projectId));
  return response.data as QueueState;
}

/**
 * Update a queued job's message text, settings, and model (saved when the user clicks "Save Changes").
 * Editing is local-only (client-side) — it never sets a blocking intervention on the server.
 */
export async function updateQueueJob(
  projectId: string,
  jobId: string,
  payload: {
    user_message?: string;
    settings?: Record<string, boolean>;
    model_preference?: string;
    interaction_mode?: string;
    file_ids?: string[];
    artifact_ids?: string[];
    image_ids?: string[];
    attachments?: any[];
  },
  base: QueueBase = DEV_QUEUE_BASE
): Promise<void> {
  await api.patch(`${base(projectId)}/${jobId}`, payload);
}

/**
 * Cancel a queued job. Sets status to CANCELLED (soft-delete).
 */
export async function cancelQueueJob(
  projectId: string,
  jobId: string,
  base: QueueBase = DEV_QUEUE_BASE
): Promise<void> {
  await api.delete(`${base(projectId)}/${jobId}`);
}

/**
 * Resumes all PAUSED jobs back to QUEUED status.
 */
export async function resumeAllQueueJobs(
  projectId: string,
  base: QueueBase = DEV_QUEUE_BASE
): Promise<{ status: string; resumed_count: number }> {
  const response = await api.post(`${base(projectId)}/resume-all`);
  return response.data;
}
